import html2canvas from "html2canvas";
import jsPDF from "jspdf";

/**
 * Generates a compressed JPEG PDF from a given HTML element.
 * 
 * @param {HTMLElement} element The HTML element to capture.
 * @param {string} filename The name of the file (e.g. "receipt.pdf").
 * @param {string} returnType "file" to return a File object, "download" to trigger browser download.
 * @returns {Promise<File|void>}
 */
export const generatePdfFromElement = async (element, filename = "receipt.pdf", returnType = "file") => {
  // Take a snapshot using 2x scale for clarity, but compress using JPEG to save size
  const canvas = await html2canvas(element, { scale: 2, useCORS: true, backgroundColor: "#ffffff" });
  const imgData = canvas.toDataURL("image/jpeg", 0.8);
  
  const pdf = new jsPDF("p", "mm", "a4");
  const pdfWidth = pdf.internal.pageSize.getWidth();
  const pdfHeight = (canvas.height * pdfWidth) / canvas.width;
  
  pdf.addImage(imgData, "JPEG", 0, 0, pdfWidth, pdfHeight);

  if (returnType === "file") {
    const pdfBlob = pdf.output("blob");
    return new File([pdfBlob], filename, { type: "application/pdf" });
  } else if (returnType === "download") {
    pdf.save(filename);
  }
};

/**
 * Captures an HTML element as an image and copies it to the user's clipboard.
 * 
 * @param {HTMLElement} element The HTML element to capture.
 * @returns {Promise<boolean>} True if successful, false otherwise.
 */
export const copyElementToClipboardAsImage = async (element) => {
  try {
    const canvas = await html2canvas(element, { scale: 2, useCORS: true, backgroundColor: "#ffffff" });
    const blob = await new Promise((resolve) => canvas.toBlob(resolve, "image/png"));
    
    await navigator.clipboard.write([
      new ClipboardItem({
        "image/png": blob
      })
    ]);
    return true;
  } catch (error) {
    console.error("Clipboard copy failed:", error);
    return false;
  }
};
